// KeyValue.jsx
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Row, Col, Typography, Tooltip } from "antd";
import { firstLetterCaps, isoDateToDate } from "../../helpers/helper";
import { getIcon } from "./FuzzyIcon";
import resolveFromStore from "../../helpers/resolvers";

const { Text } = Typography;

const styles = {
  container: { width: "100%" },
  label: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    paddingBottom: 4,
    fontSize: 12,
    lineHeight: 1,
    color: "#6B7280", // gray-500
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  valueWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  value: {
    fontSize: 16,
    color: "#111827", // gray-900
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },
  iconWrapper: { display: "inline-flex", alignItems: "center" },
};

const isDateField = (lbl) =>
  typeof lbl === "string" && (lbl.endsWith("At") || lbl.endsWith("Date"));

const KeyValue = ({ label, value, withIcon = false, tooltipThreshold = 28 }) => {
  // format raw value (dates / booleans / null)
  const formatted = useMemo(() => {
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (isDateField(label)) {
      const d = isoDateToDate(value);
      return d || "—";
    }
    return value;
  }, [label, value]);

  // If there is a resolver, use it. Use try/catch to avoid throw.
  const resolved = useMemo(() => {
    try {
      if (label && Object.prototype.hasOwnProperty.call(resolveFromStore, label)) {
        const fn = resolveFromStore[label];
        if (typeof fn === "function") {
          // give resolver a friendly input (capitalized)
          const out = fn(firstLetterCaps(String(formatted)));
          return out ?? String(formatted);
        }
      }
    } catch (e) {
      // swallow resolver errors and fall back to raw formatted
      // console.warn(e);
    }
    return String(formatted);
  }, [label, formatted]);

  const displayText = firstLetterCaps(resolved);

  const needsTooltip = String(displayText).length > tooltipThreshold;

  return (
    <div style={styles.container} aria-label={label ? `${label}-field` : undefined}>
      <div style={styles.label}>
        {withIcon && label ? (
          <span style={styles.iconWrapper} aria-hidden="true">
            {getIcon(label, true, { size: 18, title: firstLetterCaps(label) })}
          </span>
        ) : null}
        
      </div>

      <div style={styles.valueWrap}>
        {needsTooltip ? (
          <Tooltip title={displayText} placement="topLeft">
            <Text style={styles.value} ellipsis={{ tooltip: false }}>
              {displayText}
            </Text>
          </Tooltip>
        ) : (
          <Text style={styles.value}>{displayText}</Text>
        )}
      </div>
    </div>
  );
};

KeyValue.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any,
  withIcon: PropTypes.bool,
  tooltipThreshold: PropTypes.number,
};

KeyValue.defaultProps = {
  label: "",
  value: "",
  withIcon: false,
  tooltipThreshold: 28,
};

export default KeyValue;
